import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Globe, Check } from 'lucide-react';

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { language, setLanguage, availableLanguages, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = availableLanguages.find(l => l.code === language);

  // Compact variant for mobile headers
  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium leading-relaxed"
          aria-label="Change language"
        >
          <Globe className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <span className="text-gray-700">{language.toUpperCase()}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 overflow-hidden">
            <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase leading-relaxed">{t('language')}</p>
            </div>
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-3 text-left flex items-center justify-between hover:bg-green-50 transition-colors ${
                  language === lang.code ? 'bg-green-50' : ''
                }`}
              >
                <div className="leading-relaxed">
                  <span className={`font-medium block ${language === lang.code ? 'text-green-700' : 'text-gray-700'}`}>
                    {lang.nativeName}
                  </span>
                  <span className="text-xs text-gray-500">({lang.name})</span>
                </div>
                {language === lang.code && (
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 hover:border-green-400 transition-all shadow-sm leading-relaxed min-h-[2.75rem]"
        aria-label="Change language"
      >
        <Globe className="w-4 h-4 text-green-600 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-700 leading-relaxed">{currentLang?.nativeName}</span>
        <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border border-gray-200 py-1 z-50 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-100">
            <p className="text-xs font-semibold text-green-700 uppercase flex items-center gap-2 leading-relaxed">
              <Globe className="w-3.5 h-3.5 flex-shrink-0" />
              {t('language')}
            </p>
          </div>
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3.5 text-left flex items-center justify-between hover:bg-green-50 transition-colors ${
                language === lang.code ? 'bg-green-50' : ''
              }`}
            >
              <div className="flex flex-col leading-relaxed">
                <span className={`font-semibold leading-relaxed ${language === lang.code ? 'text-green-700' : 'text-gray-800'}`}>
                  {lang.nativeName}
                </span>
                <span className="text-xs text-gray-500 leading-relaxed">{lang.name}</span>
              </div>
              {language === lang.code && (
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
